import { useState, useEffect } from "react";
import type { AgentCard } from "../api";
import { ChevronRight, ChevronLeft, Phone } from "./icons";

interface Props {
  /** The full agent list is still passed in for layout decisions (empty state),
   *  but per client direction only the `selected` agent is rendered — the
   *  consumer page is for ONE LO + ONE co-branding partner, no picker. */
  agents: AgentCard[];
  selected: AgentCard | null;
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ agents, selected, collapsed, onToggle }: Props) {
  // Track headshot load failures so we can fall back to the initials
  // placeholder when the URL is dead. Reset whenever the selected agent
  // changes so a fresh URL gets a fresh chance to load.
  const [photoFailed, setPhotoFailed] = useState(false);
  useEffect(() => { setPhotoFailed(false); }, [selected?.agent_headshot_url]);

  if (collapsed) {
    return (
      <aside className="sidebar sidebar--collapsed" aria-label="Referral partner">
        <button className="sidebar__expand" onClick={onToggle} aria-label="Show referral partner">
          <ChevronLeft />
          <span className="sidebar__expand-label">Partner</span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="sidebar" aria-label="Referral partner">
      <div className="sidebar__head">
        <div className="sidebar__title">
          <span className="sidebar__eyebrow">Your referral partner</span>
        </div>
        <button className="icon-btn icon-btn--sm" onClick={onToggle} aria-label="Hide referral partner">
          <ChevronRight />
        </button>
      </div>

      {agents.length === 0 ? (
        <div className="sidebar__empty">
          <div className="sidebar__empty-icon">···</div>
          <div className="sidebar__empty-text">Referral partner not set</div>
          <div className="sidebar__empty-sub">Your loan officer hasn't added one yet.</div>
        </div>
      ) : (
        selected && (
          <>
            <div className="agent-card">
              <div className="agent-card__cover" aria-hidden="true" />
              {selected.agent_headshot_url && !photoFailed ? (
                <img
                  className="agent-card__photo"
                  src={selected.agent_headshot_url}
                  alt=""
                  onError={() => setPhotoFailed(true)}
                />
              ) : (
                <div className="agent-card__photo agent-card__photo--placeholder">
                  {selected.label.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="agent-card__name">{selected.label}</div>
              <div className="agent-card__role">Referral Partner</div>

              {selected.agent_license && (
                <div className="agent-card__license">Lic #{selected.agent_license}</div>
              )}

              {selected.agent_phone && (
                <a className="agent-card__phone" href={`tel:${selected.agent_phone.replace(/[^\d+]/g, "")}`}>
                  <Phone size={14} />
                  <span>{selected.agent_phone}</span>
                </a>
              )}
              {selected.agent_email && (
                <a className="agent-card__email" href={`mailto:${selected.agent_email}`}>
                  <span>{selected.agent_email}</span>
                </a>
              )}
            </div>

            {/* Per client feedback: only the agent named in the URL is shown.
                The consumer page is for ONE LO + ONE co-branding partner. */}
          </>
        )
      )}
    </aside>
  );
}
